import { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { showAlert } from '@/components/ui/GlobalAlert';
import {
  Landmark, Link2, RefreshCw, Unlink, Loader2, Info, Clock, CheckCircle2,
  AlertTriangle, FileSearch, ExternalLink, Globe
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

const ESTADO_COLOR = {
  conectado: 'text-green-400', error: 'text-red-400', desconectado: 'text-[#888]',
};

function fmtFecha(f) {
  if (!f) return '—';
  try { return new Date(f).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(f); }
}

export default function SincronizacionJudicial() {
  const [sistemas, setSistemas] = useState([]);
  const [portales, setPortales] = useState([]);
  const [fuentes, setFuentes] = useState([]);
  const [expedientes, setExpedientes] = useState([]);
  const [log, setLog] = useState([]);

  const [conn, setConn] = useState({ sistema: '', identificador: '', password: '' });
  const [vinc, setVinc] = useState({ expediente_id: '', sistema: '', referencia: '' });
  const [consultaR, setConsultaR] = useState({ clave: '', numero: '' });
  const [guardando, setGuardando] = useState(false);
  const [syncing, setSyncing] = useState('');

  const cargar = useCallback(async () => {
    try {
      const [s, p, f, e, l] = await Promise.all([
        api.get('/api/judicial/sistemas'),
        api.get('/api/judicial/portales-consulta'),
        api.get('/api/judicial/fuentes'),
        api.get('/api/expedientes/'),
        api.get('/api/judicial/sync-log'),
      ]);
      setSistemas(s.data || []); setPortales(p.data || []); setFuentes(f.data || []);
      setExpedientes(e.data || []); setLog(l.data || []);
      if (s.data?.[0] && !conn.sistema) setConn((c) => ({ ...c, sistema: s.data[0].sistema }));
    } catch { /* noop */ }
  }, [conn.sistema]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const sistemaSel = sistemas.find((s) => s.sistema === conn.sistema);
  const esPublico = !!sistemaSel?.publico;

  const conectar = async () => {
    if (!conn.sistema) { showAlert('Elegí un sistema.', 'warning'); return; }
    if (!esPublico && (!conn.identificador || !conn.password)) {
      showAlert('Completá usuario y contraseña.', 'warning'); return;
    }
    setGuardando(true);
    try {
      await api.post('/api/judicial/fuentes', conn);
      showAlert('Portal conectado. Las credenciales se guardaron cifradas.', 'success');
      setConn({ ...conn, identificador: '', password: '' });
      cargar();
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al conectar el portal.', 'error');
    } finally { setGuardando(false); }
  };

  const desconectar = async (id) => {
    try { await api.delete(`/api/judicial/fuentes/${id}`); cargar(); }
    catch { showAlert('Error al desconectar.', 'error'); }
  };

  const sincronizar = async (id) => {
    setSyncing(id);
    try {
      await api.post(`/api/judicial/fuentes/${id}/sync`);
      showAlert('Sincronización iniciada. Revisá el historial en unos segundos.', 'info');
      setTimeout(cargar, 2500);
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al sincronizar.', 'error');
    } finally { setSyncing(''); }
  };

  const vincular = async () => {
    if (!vinc.expediente_id || !vinc.sistema || !vinc.referencia) {
      showAlert('Elegí expediente, sistema y número de referencia.', 'warning'); return;
    }
    try {
      await api.post(`/api/judicial/expedientes/${vinc.expediente_id}/vincular`, {
        sistema: vinc.sistema, referencia: vinc.referencia,
      });
      showAlert('Expediente vinculado.', 'success');
      setVinc({ expediente_id: '', sistema: '', referencia: '' });
    } catch (e) {
      showAlert(e.response?.data?.detail || 'Error al vincular.', 'error');
    }
  };

  // Construye la URL del portal: precarga el número donde el portal lo permite.
  const urlPortal = (portal, ref) =>
    portal.prefill && ref
      ? portal.url.replace('{ref}', encodeURIComponent(ref.trim()))
      : portal.url;

  const abrirPortal = (portal, ref) => {
    if (!portal) return;
    if (portal.prefill && !String(ref || '').trim()) {
      showAlert('Escribí el número de expediente.', 'warning'); return;
    }
    window.open(urlPortal(portal, ref), '_blank', 'noopener,noreferrer');
  };

  const portalSel = portales.find((p) => p.clave === consultaR.clave);
  const vinculados = expedientes.filter((x) => x.sync_sistema && x.sync_referencia);

  return (
    <DashboardLayout title="Sincronización Judicial">
      {/* Aviso de estado de desarrollo */}
      <div className="flex items-start gap-3 px-4 py-3 mb-5 rounded-xl border border-[#1E3A8A]/40 bg-[#1E3A8A]/[0.08]">
        <Info className="w-5 h-5 text-[#7AA2F7] shrink-0 mt-0.5" />
        <p className="text-[13px] text-[#D8D1C3] leading-relaxed">
          Conectá los portales judiciales para traer los movimientos de tus expedientes automáticamente.
          <strong className="text-[#9FB8F0]"> Misiones y CABA ya están operativos</strong> (consulta pública, sin credenciales).
          El resto de las provincias se consultan con un clic desde <strong className="text-[#9FB8F0]">Consulta rápida</strong> (abajo),
          que abre el portal oficial con el expediente precargado. Los portales que piden login se activan al completar su adaptador.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Conectar portal */}
        <div className="rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
          <h3 className="text-[#C9A44C] text-sm font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Landmark className="w-4 h-4" /> Conectar portal
          </h3>
          <div className="space-y-3">
            <select value={conn.sistema} onChange={(e) => setConn({ ...conn, sistema: e.target.value })} className={IC}>
              {sistemas.map((s) => <option key={s.sistema} value={s.sistema} className="bg-[#141414]">{s.label}</option>)}
            </select>
            {esPublico ? (
              <p className="text-[11px] text-[#777] flex items-center gap-1.5">
                <Info className="w-3 h-3 text-[#7AA2F7]" /> Portal de consulta pública: no requiere credenciales. Se consulta por número de expediente al vincularlo.
              </p>
            ) : (
              <>
                <input value={conn.identificador} onChange={(e) => setConn({ ...conn, identificador: e.target.value })}
                  className={IC} placeholder="Usuario / CUIT del portal" />
                <input type="password" value={conn.password} onChange={(e) => setConn({ ...conn, password: e.target.value })}
                  className={IC} placeholder="Contraseña del portal" />
                <p className="text-[11px] text-[#777] flex items-center gap-1.5">
                  <AlertTriangle className="w-3 h-3 text-[#C9A44C]" /> Las credenciales se guardan cifradas y solo se usan para sincronizar tus expedientes.
                </p>
              </>
            )}
            <button onClick={conectar} disabled={guardando}
              className="w-full text-[#070707] font-semibold px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm disabled:opacity-50"
              style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
              {guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Conectar
            </button>
          </div>
        </div>

        {/* Vincular expediente */}
        <div className="rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
          <h3 className="text-[#C9A44C] text-sm font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <FileSearch className="w-4 h-4" /> Vincular expediente a un sistema
          </h3>
          <div className="space-y-3">
            <select value={vinc.expediente_id} onChange={(e) => setVinc({ ...vinc, expediente_id: e.target.value })} className={IC}>
              <option value="" className="bg-[#141414]">— Expediente —</option>
              {expedientes.map((x) => <option key={x.id} value={x.id} className="bg-[#141414]">{x.caratula}</option>)}
            </select>
            <select value={vinc.sistema} onChange={(e) => setVinc({ ...vinc, sistema: e.target.value })} className={IC}>
              <option value="" className="bg-[#141414]">— Sistema —</option>
              {sistemas.map((s) => <option key={s.sistema} value={s.sistema} className="bg-[#141414]">{s.label}</option>)}
            </select>
            <input value={vinc.referencia} onChange={(e) => setVinc({ ...vinc, referencia: e.target.value })}
              className={IC} placeholder="Número de expediente en el portal" />
            <button onClick={vincular}
              className="w-full px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm border border-[#C9A44C]/40 text-[#E0C878] hover:bg-[#C9A44C]/10">
              <Link2 className="w-4 h-4" /> Vincular
            </button>
          </div>
        </div>
      </div>

      {/* Consulta rápida (deep-link al portal oficial) */}
      <div className="mt-6 rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
        <h3 className="text-[#C9A44C] text-sm font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Globe className="w-4 h-4" /> Consulta rápida en el portal oficial
        </h3>
        <p className="text-[11px] text-[#777] mb-3">
          Elegí la provincia y, donde el portal lo permite, abre la causa con el número ya cargado. Cubre todas las jurisdicciones.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <select value={consultaR.clave} onChange={(e) => setConsultaR({ ...consultaR, clave: e.target.value })}
            className={IC + ' sm:max-w-[240px]'}>
            <option value="" className="bg-[#141414]">— Provincia / fuero —</option>
            {portales.map((p) => (
              <option key={p.clave} value={p.clave} className="bg-[#141414]">
                {p.label}{p.prefill ? '' : ' (sin precarga)'}
              </option>
            ))}
          </select>
          <input value={consultaR.numero} onChange={(e) => setConsultaR({ ...consultaR, numero: e.target.value })}
            className={IC} placeholder={portalSel?.prefill ? 'Número de expediente / CUIJ' : 'Abre el portal (tipeás el número ahí)'}
            disabled={portalSel && !portalSel.prefill} />
          <button onClick={() => abrirPortal(portalSel, consultaR.numero)} disabled={!portalSel}
            className="shrink-0 px-4 py-2.5 rounded-lg flex items-center justify-center gap-2 text-sm border border-[#C9A44C]/40 text-[#E0C878] hover:bg-[#C9A44C]/10 disabled:opacity-40">
            <ExternalLink className="w-4 h-4" /> Abrir portal
          </button>
        </div>
      </div>

      {/* Expedientes vinculados — acceso directo al portal */}
      {vinculados.length > 0 && (
        <div className="mt-6 rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
          <h3 className="text-[#C9A44C] text-sm font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <FileSearch className="w-4 h-4" /> Expedientes vinculados
          </h3>
          <div className="space-y-2">
            {vinculados.map((x) => {
              const portal = portales.find((p) => p.clave === x.sync_sistema);
              return (
                <div key={x.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                  <div className="min-w-0">
                    <div className="text-sm text-[#F8F6F0] truncate">{x.caratula}</div>
                    <div className="text-[11px] text-[#888] mt-0.5 truncate">
                      {(portal?.label || sistemas.find((s) => s.sistema === x.sync_sistema)?.label || x.sync_sistema)} · {x.sync_referencia}
                    </div>
                  </div>
                  <button onClick={() => abrirPortal(portal, x.sync_referencia)} disabled={!portal}
                    className="shrink-0 flex items-center gap-1.5 text-xs text-[#E0C878] hover:text-[#C9A44C] disabled:opacity-40">
                    <ExternalLink className="w-3.5 h-3.5" /> Ver en portal
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Portales conectados */}
      <div className="mt-6 rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
        <h3 className="text-[#C9A44C] text-sm font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>Portales conectados</h3>
        {fuentes.length === 0 ? (
          <p className="text-[#666] text-sm">No conectaste ningún portal todavía.</p>
        ) : (
          <div className="space-y-2">
            {fuentes.map((f) => (
              <div key={f.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.02]">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm text-[#F8F6F0]">
                    <span className={`w-2 h-2 rounded-full ${f.estado === 'conectado' ? 'bg-green-400' : f.estado === 'error' ? 'bg-red-400' : 'bg-[#666]'}`} />
                    {(sistemas.find((s) => s.sistema === f.sistema)?.label) || f.sistema}
                    <span className="text-[#777] text-xs">· {f.identificador}</span>
                  </div>
                  <div className="text-[11px] text-[#888] mt-0.5 truncate">
                    {f.ultimo_mensaje || '—'} · última sync: {fmtFecha(f.ultima_sync)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => sincronizar(f.id)} disabled={syncing === f.id}
                    className="flex items-center gap-1 text-[#C9A44C] hover:text-[#E0C878] text-xs">
                    <RefreshCw className={`w-3.5 h-3.5 ${syncing === f.id ? 'animate-spin' : ''}`} /> Sincronizar
                  </button>
                  <button onClick={() => desconectar(f.id)} className="text-[#666] hover:text-red-400">
                    <Unlink className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Historial */}
      <div className="mt-6 rounded-xl border border-white/10 bg-[#0E0E0E] p-5">
        <h3 className="text-[#C9A44C] text-sm font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Clock className="w-4 h-4" /> Historial de sincronización
        </h3>
        {log.length === 0 ? (
          <p className="text-[#666] text-sm">Sin sincronizaciones aún.</p>
        ) : (
          <div className="space-y-1.5">
            {log.map((l) => (
              <div key={l.id} className="flex items-center gap-3 text-xs text-[#aaa] py-1.5 border-b border-white/5 last:border-0">
                {l.estado === 'ok' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-400 shrink-0" />
                  : l.estado === 'error' ? <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                  : <Clock className="w-3.5 h-3.5 text-[#C9A44C] shrink-0" />}
                <span className="text-[#777] w-28 shrink-0">{fmtFecha(l.inicio)}</span>
                <span className="w-16 shrink-0 uppercase text-[10px]">{l.sistema}</span>
                <span className="flex-1 truncate">{l.mensaje || l.estado}</span>
                {l.movimientos_nuevos > 0 && <span className="text-green-400">+{l.movimientos_nuevos}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
